<template>
  <div class="page-wrapper">
    <div class="page-header">
      <h2 class="page-title">Sinatec Stock</h2>
      <div class="header-controls">
        <div class="filter-item">
          <label for="low-stock-threshold">Low stock threshold:</label>
          <input
            id="low-stock-threshold"
            type="number"
            min="0"
            v-model.number="lowStockThreshold"
            class="search-input threshold-input"
          />
        </div>
        <button @click="fetchStocks" class="refresh-button" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ spinning: loading }"></i> Refresh
        </button>
      </div>
    </div>

    <!-- Low Stock Section -->
    <div class="section low-stock-section">
      <div class="section-header">
        <h3 class="section-title">Running Low ({{ lowStockItems.length }})</h3>
      </div>

      <div v-if="loading" class="loading-placeholder">
        <p>Loading...</p>
      </div>
      <div v-else-if="lowStockItems.length === 0" class="no-data">
        <p>No products at or below the threshold of {{ lowStockThreshold }}.</p>
      </div>
      <div v-else class="table-wrapper">
        <table class="modern-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Sinatec Code</th>
              <th
                class="sortable"
                @click="handleSort('quantity')"
              >
                Quantity
                <span v-if="sortBy === 'quantity'" class="sort-icon">{{ sortDesc ? '▼' : '▲' }}</span>
              </th>
              <th>Yesterday</th>
              <th>1 Week Ago</th>
              <th>Last Checked</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in sortedLowStockItems" :key="item.synatec_code" class="low-stock-row">
              <td>{{ item.sinatec_name || '-' }}</td>
              <td>{{ item.synatec_code }}</td>
              <td class="quantity-cell low">{{ formatQuantity(item.quantity) }}</td>
              <td>{{ formatQuantity(item.quantity_yesterday) }}</td>
              <td>{{ formatQuantity(item.quantity_week_ago) }}</td>
              <td>{{ formatDate(item.checked_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- All Stock Section -->
    <div class="section">
      <div class="section-header">
        <h3 class="section-title">All Products ({{ stocks.length }})</h3>
      </div>

      <div v-if="loading" class="loading-placeholder">
        <p>Loading...</p>
      </div>
      <div v-else-if="stocks.length === 0" class="no-data">
        <p>No stock data found yet.</p>
      </div>
      <div v-else class="table-wrapper">
        <table class="modern-table">
          <thead>
            <tr>
              <th class="sortable" @click="handleSort('sinatec_name')">
                Name
                <span v-if="sortBy === 'sinatec_name'" class="sort-icon">{{ sortDesc ? '▼' : '▲' }}</span>
              </th>
              <th class="sortable" @click="handleSort('synatec_code')">
                Sinatec Code
                <span v-if="sortBy === 'synatec_code'" class="sort-icon">{{ sortDesc ? '▼' : '▲' }}</span>
              </th>
              <th class="sortable" @click="handleSort('quantity')">
                Quantity
                <span v-if="sortBy === 'quantity'" class="sort-icon">{{ sortDesc ? '▼' : '▲' }}</span>
              </th>
              <th class="sortable" @click="handleSort('quantity_yesterday')">
                Yesterday
                <span v-if="sortBy === 'quantity_yesterday'" class="sort-icon">{{ sortDesc ? '▼' : '▲' }}</span>
              </th>
              <th class="sortable" @click="handleSort('quantity_week_ago')">
                1 Week Ago
                <span v-if="sortBy === 'quantity_week_ago'" class="sort-icon">{{ sortDesc ? '▼' : '▲' }}</span>
              </th>
              <th class="sortable" @click="handleSort('checked_at')">
                Last Checked
                <span v-if="sortBy === 'checked_at'" class="sort-icon">{{ sortDesc ? '▼' : '▲' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in sortedStocks"
              :key="item.synatec_code"
              :class="{ 'low-stock-row': isLowStock(item) }"
            >
              <td>{{ item.sinatec_name || '-' }}</td>
              <td>{{ item.synatec_code }}</td>
              <td class="quantity-cell" :class="{ low: isLowStock(item) }">{{ formatQuantity(item.quantity) }}</td>
              <td>{{ formatQuantity(item.quantity_yesterday) }}</td>
              <td>{{ formatQuantity(item.quantity_week_ago) }}</td>
              <td>{{ formatDate(item.checked_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "Stock",
  data() {
    return {
      stocks: [],
      loading: false,
      lowStockThreshold: 10,
      sortBy: "quantity",
      sortDesc: false
    };
  },
  computed: {
    lowStockItems() {
      return this.stocks.filter((item) => this.isLowStock(item));
    },
    sortedStocks() {
      return this.sortItems(this.stocks);
    },
    sortedLowStockItems() {
      return this.sortItems(this.lowStockItems);
    }
  },
  mounted() {
    this.fetchStocks();
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),

    isLowStock(item) {
      return item.quantity !== null && item.quantity !== undefined && item.quantity <= this.lowStockThreshold;
    },

    handleSort(column) {
      if (this.sortBy === column) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = column;
        this.sortDesc = false;
      }
    },

    sortItems(items) {
      const column = this.sortBy;
      const direction = this.sortDesc ? -1 : 1;

      return [...items].sort((a, b) => {
        let valA = a[column];
        let valB = b[column];

        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        if (typeof valA === "string") {
          return valA.localeCompare(valB) * direction;
        }

        return (valA - valB) * direction;
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleString();
    },

    formatQuantity(quantity) {
      return quantity === null || quantity === undefined ? "N/A" : quantity;
    },

    async fetchStocks() {
      this.loading = true;
      try {
        const data = {
          query: `
            query {
              sinatecProductStocks
            }
          `
        };

        const response = await this.simpleFetch({
          data,
          this_ref: this,
          skip_confirm: true
        });

        if (response && response.sinatecProductStocks) {
          this.stocks = JSON.parse(response.sinatecProductStocks);
        }
      } catch (error) {
        console.error("Error fetching sinatec product stocks:", error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.threshold-input {
  width: 80px;
}

.section {
  margin-bottom: 24px;
}

.low-stock-section {
  border: 1px solid #fed7d7;
}

.refresh-button {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  color: #4a5568;
  font-size: 14px;
}

.refresh-button:hover:not(:disabled) {
  background: #f7fafc;
}

.refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.quantity-cell.low {
  color: #e53e3e;
  font-weight: 700;
}

.low-stock-row {
  background-color: #fff5f5;
}

.loading-placeholder,
.no-data {
  padding: 24px;
  text-align: center;
  color: #718096;
}
</style>
